/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";

// antd:
import { Form } from "antd";

// eslint-disable-next-line node/no-extraneous-import
import DOMPurify from "dompurify";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {Prism as SyntaxHighlighter} from "react-syntax-highlighter";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

import DivE from "../react/DivE.tsx";

// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getAppState, getTheme, getLng, getDict, getUser, changeAppState } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

type codeType = "html" | "md" | "txt";

const { rz } = window;

const cleanHtml = (value) => (DOMPurify.sanitize(value, {
  USE_PROFILES: { html: true, svg: true, svgFilters: true }, // Разрешает только HTML-теги
  ADD_ATTR: ["target"]
  // ALLOWED_TAGS: ["p", "br", "a", "span", "div", "ol", "ul", "li", "strong", "em", "table", "tbody", "thead", "tr", "td", "img", "svg", "g", "path", "polygon", "circle", "line"] // Какие теги оставить
}));

const HTMLMDviewer:FC<TuniversalElementProps> = ({attributes}) => {
  HTMLMDviewer.displayName = "HTMLMDviewer";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(getUser);
  const lng = useAppSelector(getLng);
  const dct = useAppSelector(getDict);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, align} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath];
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const elType = useMemo(() => {
    let typ:codeType = "txt";
    (attributes.type || attributes.format || attributes.language || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (tt === "html") { typ = "html" }
      else if (~["md", "markdown"].indexOf(tt)) { typ = "md" }
    });

    if (rz.isString(elState.value) && (elState.value || "").trim().startsWith("<") && (elState.value || "").trim().endsWith(">")) { return "html" }

    return typ;
  }, [attributes.format, attributes.language, attributes.type, elState.value]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    // if (!rz.arrContains(userClss, (/-theme-/igm))) { userClss.push("rz-theme-d5") }
    return userClss;
  }, [elState?.userClass, elState?.disabled, permissions]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    return est;
  }, [elState.userStyle, elState?.visible, permissions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, attributes, lng]);

  const codeComponents = useMemo(() => ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    code({ node, inline, className, children, ...props1 }: any) {
      const match = /language-(\w+)/.exec(className || "");
      return !inline && match ? (
        // style={theme === "light" ? light : dark}
        <SyntaxHighlighter PreTag="div" language={match[1]} {...props1}>
          {String(children).replace(/\n$/, "")}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props1}>
          {children}
        </code>
      );
    }
  }), []);

  const viewContent = useCallback((edType:string, edContent:string) => {
    if (edType === "html") {
      return (
        <div key={"viewer-div-content-" + key} id={"viewer-div-content-" + key}
          dangerouslySetInnerHTML={{__html: cleanHtml(edContent || "")}}
        />
      );
    }
    if (edType === "md") {
      return (
        <Markdown key={"viewer-div-content-" + key}
          remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={codeComponents}
        >
          {edContent || ""}
        </Markdown>
      );
    }
    return (
      <div key={"viewer-div-content-" + key} id={"viewer-div-content-" + key}>
        {edContent || ""}
      </div>
    );
  }, [key, codeComponents]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      // eslint-disable-next-line max-len
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  return wrapperForm(
    <DivE key={key} id={key} className={clsu.join(" ")} style={styl} >
      {viewContent(elType, (rz.isString(elState?.value) ? elState.value : ""))}
    </DivE>
  );
}

export default HTMLMDviewer;
/* eslint-enable @typescript-eslint/no-unused-vars */
